import { useMemo } from "react";
import { useWorkspaceData } from "@data";
import { useWorkspace } from "../context/WorkspaceContext";
import { WorkspaceRole } from "../types";

export interface OnboardingStatus {
  hasCompany: boolean;
  hasVehicle: boolean;
  hasDriver: boolean;
  hasCustomer: boolean;
  hasProduct: boolean;
  hasLocation: boolean;
  /**
   * The initial wizard only requires a company. The workspace itself (plus its
   * default vehicle/product resource types) is created automatically on signup,
   * so a company is the single piece of data a user must provide before the app
   * is usable. Everything else is guided from inside the app.
   *
   * This is false when the company fetch failed (we can't tell an unconfigured
   * workspace from a transient error) or when the current user's role can't
   * create a company anyway, so we never trap someone in a wizard they cannot
   * complete.
   */
  requiresInitialSetup: boolean;
  /**
   * Whether enough data exists to start an order. A driver must already exist
   * (it cannot be created inside the order wizard), while a customer can be
   * created inline in the customer step — so only the driver is gated here.
   */
  canCreateOrder: boolean;
  /**
   * True while any dataset a gating decision depends on is still loading.
   * Consumers must NOT act on the flags while this is true, to avoid
   * redirecting/blocking a user whose data simply has not arrived yet.
   */
  isResolving: boolean;
}

// Only these roles are allowed (by the backend) to create a company, which is
// the sole action of the initial setup wizard.
const CAN_CREATE_COMPANY_ROLES: WorkspaceRole[] = [
  WorkspaceRole.Owner,
  WorkspaceRole.Administrator,
];

/**
 * Derives onboarding progress purely from the workspace's existing master data.
 * No extra persistence is needed: completion is simply "does the entity exist".
 */
export const useOnboardingStatus = (): OnboardingStatus => {
  const {
    companies,
    vehicles,
    drivers,
    customers,
    products,
    locations,
    loading,
    error,
  } = useWorkspaceData();
  const { activeWorkspace } = useWorkspace();
  const role = activeWorkspace?.role;
  const workspaceId = activeWorkspace?.workspaceId;

  return useMemo<OnboardingStatus>(() => {
    const hasCompany = companies.length > 0;
    const hasVehicle = vehicles.length > 0;
    const hasDriver = drivers.length > 0;
    const hasCustomer = customers.length > 0;
    const hasProduct = products.length > 0;
    const hasLocation = locations.length > 0;

    // A missing role (e.g. not yet resolved) defaults to "allowed" so brand-new
    // owners are never skipped; explicit non-admin roles are excluded.
    const canManageCompany =
      role === undefined || CAN_CREATE_COMPANY_ROLES.includes(role);

    // Never send an existing workspace to onboarding because a fetch failed, or
    // because the user's role can't create a company in the first place. Also
    // require an active workspace: with none selected there is no company data
    // to fetch (the store serves empty defaults), and treating that emptiness as
    // "needs setup" would wrongly bounce transient no-workspace states (e.g. the
    // "New workspace" action) into the wizard.
    const requiresInitialSetup =
      Boolean(workspaceId) &&
      !hasCompany &&
      !error.companies &&
      canManageCompany;

    // Don't block order creation on a failed drivers fetch either.
    const canCreateOrder = hasDriver || error.drivers;

    // Gating decisions depend on companies (initial setup) and drivers (order
    // creation), so wait for those before acting on the flags.
    const isResolving = loading.companies || loading.drivers;

    return {
      hasCompany,
      hasVehicle,
      hasDriver,
      hasCustomer,
      hasProduct,
      hasLocation,
      requiresInitialSetup,
      canCreateOrder,
      isResolving,
    };
  }, [
    companies.length,
    vehicles.length,
    drivers.length,
    customers.length,
    products.length,
    locations.length,
    loading.companies,
    loading.drivers,
    error.companies,
    error.drivers,
    role,
    workspaceId,
  ]);
};

export default useOnboardingStatus;
